/**
 * Room-price helpers used by Directory, Dashboard and Calendar so a room with
 * `occupancy_prices` shows the tier price instead of the raw `price_per_night`.
 *
 * Rule (mirrors backend `base_room_price` in server.py):
 *   - When `occupants` is given, return the highest tier with `occupants <= n`.
 *   - Without `occupants`, use the tier for the room's `capacity` (peak price).
 *   - Fallback to `price_per_night` when no tier fits.
 */
export function roomHeadlinePrice(room, occupants) {
  if (!room) return { price: 0, hasTiers: false, fromRange: false };
  const tiers = Array.isArray(room.occupancy_prices) ? room.occupancy_prices : [];
  const base = Number(room.price_per_night) || 0;
  if (!tiers.length) return { price: base, hasTiers: false, fromRange: false };

  const target = occupants ? Number(occupants) : Number(room.capacity) || 0;
  const prices = tiers.map((t) => Number(t.price) || 0).filter((p) => p > 0);
  const distinctPrices = new Set(prices);
  const fromRange = distinctPrices.size > 1;

  if (target > 0) {
    const fits = tiers.filter((t) => Number(t.occupants || 0) <= target);
    if (fits.length) {
      const best = fits.reduce((a, b) =>
        Number(b.occupants || 0) > Number(a.occupants || 0) ? b : a
      );
      const p = Number(best.price) || base;
      return { price: p, hasTiers: true, fromRange };
    }
  }
  // Fallback: cheapest tier so we still surface an occupancy-based price
  const minTier = tiers.reduce((a, b) =>
    Number(b.price || Infinity) < Number(a.price || Infinity) ? b : a
  );
  return { price: Number(minTier.price) || base, hasTiers: true, fromRange };
}
